import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  StyledWinningCriteriaSectionHeader,
  StyledWinningCriteriaWrapper,
  StyledErrorMessage,
  StyledWinningCriteriaDropdown,
  StyledInfoTooltip,
} from 'modules/experiments/components/pages/edit/sections/settings/styled';
import { UpdateExperimentSettingsFormFieldNames } from 'modules/experiments/constants/updateExperimentSettings';
import {
  getSystemEventSelectOptions,
  generateCustomEventSelectionOptions,
} from 'modules/experiments/utils/WinningCriteriaForm';
import { BasicSelectField } from 'lib/iterable-hook-form';
import {
  getTakeoverCampaignConversions,
  getTakeoverCampaignMessageMedium,
} from 'modules/experiments/ducks/selectors';
import { useTakeoverExperimentContext } from 'modules/experiments/contexts';

export const WinningCriteriaForm = ({ isFirstSection }: { isFirstSection?: boolean }) => {
  const { t } = useExperimentsTranslations();
  const { isBlastCampaign, isSTOExperiment } = useTakeoverExperimentContext();

  const {
    trigger,
    formState: { errors },
  } = useFormContext();

  const { HAS_HOLDOUT_GROUP, WINNING_CRITERIA_NAME } = UpdateExperimentSettingsFormFieldNames;

  const campaignConversions = useSelector(getTakeoverCampaignConversions);
  const campaignMessageMedium = useSelector(getTakeoverCampaignMessageMedium);

  const [watchHasHoldOutGroup, watchingWinningCriteriaName] = useWatch({
    name: [HAS_HOLDOUT_GROUP, WINNING_CRITERIA_NAME],
  });

  useEffect(() => {
    trigger();
  }, [watchHasHoldOutGroup, watchingWinningCriteriaName]);

  // disable System events if 'Add holdout group...' is checked and campaign type is blast
  const isSystemEventsDisabled = watchHasHoldOutGroup === true && isBlastCampaign;

  const systemEventSelectOptions = getSystemEventSelectOptions(
    t,
    campaignMessageMedium,
    isSystemEventsDisabled,
  );
  const customEventSelectOptions = generateCustomEventSelectionOptions(campaignConversions);

  return (
    <>
      <StyledWinningCriteriaSectionHeader isFirstSection={isFirstSection}>
        {t('settings.winningCriteria.header')}
      </StyledWinningCriteriaSectionHeader>
      <StyledWinningCriteriaWrapper data-test="winning-criteria-settings">
        <div>{t('settings.winningCriteria.winnerDeterminedBy')}</div>
        <StyledWinningCriteriaDropdown>
          <StyledInfoTooltip isEnabled={isSTOExperiment} tooltipContent={t('settings.stoTooltip')}>
            <BasicSelectField
              name={WINNING_CRITERIA_NAME}
              id={`winning-criteria-select-options`}
              options={[...systemEventSelectOptions, ...customEventSelectOptions]}
              isDisabled={isSTOExperiment}
              placeholder={t('settings.stoTooltip')}
              automaticallyAvoidCollisions
              isFullWidth={true}
            />
          </StyledInfoTooltip>
          {!!errors[WINNING_CRITERIA_NAME] && (
            <StyledErrorMessage data-test="winning-criteria-error-message">
              {/* @ts-expect-error */}
              {errors[WINNING_CRITERIA_NAME]?.message}
            </StyledErrorMessage>
          )}
        </StyledWinningCriteriaDropdown>
      </StyledWinningCriteriaWrapper>
    </>
  );
};
